import { useCallback, useEffect, useState } from "react";
import {
  Paper, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, Tabs, Tab, TextField,
  TableFooter, TablePagination, Container, MenuItem, Select,
  FormControl, InputLabel, Button, Alert, LinearProgress
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfMonth, endOfMonth } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  API_STUDENT_FEE_REPORTS,
  API_MISC_FEE_REPORTS,
  API_GET_ACADEMIC_SESSIONS,
} from "../Config/ApiUrlConstant";
import { useAuth } from "../AuthContext";

const formatColumnHeader = (col) =>
  col.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
const ReportTable = ({
  title,
  data,
  columns,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  canExport,
}) => {
  const totals = {};
  columns.forEach((col) => {
    if (data.length && !isNaN(data[0][col])) {
      totals[col] = data.reduce((sum, row) => sum + Number(row[col] || 0), 0);
    }
  });

  // ✅ Export to Excel function
  const exportToExcel = () => {
    const worksheetData = data.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[formatColumnHeader(col)] = row[col];
      });
      return obj;
    });

    // Add totals row
    const totalsRow = {};
    columns.forEach((col) => {
      totalsRow[formatColumnHeader(col)] =
        totals[col] !== undefined ? totals[col] : "";
    });
    worksheetData.push(totalsRow);

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, title);

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), `${title}.xlsx`);
  };

  return (
    <Paper sx={{ p: 2, mb: 4 }}>
      <Grid container justifyContent="space-between"  >
        <Typography variant="h6" style={{ float: "left" }}>{title}</Typography>
        {canExport && <Button variant="contained" onClick={exportToExcel} style={{ float: "right", width: '200px' }} >
          Export to Excel
        </Button>}
      </Grid>

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col}>{formatColumnHeader(col)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col}>{row[col]}</TableCell>
                  ))}
                </TableRow>
              ))}
            {!data.length && <TableRow><TableCell colSpan={columns.length} align="center">No report records match the selected filters.</TableCell></TableRow>}
            {data.length > 0 && <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: "bold" }}>
                  {totals[col] !== undefined ? totals[col] : ""}
                </TableCell>
              ))}
            </TableRow>}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default function AllFeeReport() {
  const { logout, can } = useAuth(); const canExport = can('REPORTS.EXPORT');

  const today = new Date();
  const defaultSessionStart =
    today.getMonth() + 1 >= 4
      ? new Date(today.getFullYear(), 3, 1)
      : new Date(today.getFullYear() - 1, 3, 1);
  const defaultSessionEnd = new Date(today.getFullYear(), today.getMonth(), 1);

  const [tabIndex, setTabIndex] = useState(0);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [feeReport, setFeeReport] = useState([]);
  const [miscReport, setMiscReport] = useState([]);
  const [searchStudent, setSearchStudent] = useState("");
  const [searchFather, setSearchFather] = useState("");
  const [sessionStart, setSessionStart] = useState(defaultSessionStart);
  const [sessionEnd, setSessionEnd] = useState(defaultSessionEnd);
  const [paymentStatus, setPaymentStatus] = useState("All");
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedFeeSessionId, setSelectedFeeSessionId] = useState('');
  const [feeLoading, setFeeLoading] = useState(false);
  const [miscLoading, setMiscLoading] = useState(false);
  const [feeError, setFeeError] = useState('');
  const [miscError, setMiscError] = useState('');

  const [pageFee, setPageFee] = useState(0);
  const [rowsPerPageFee, setRowsPerPageFee] = useState(10);
  const [pageMisc, setPageMisc] = useState(0);
  const [rowsPerPageMisc, setRowsPerPageMisc] = useState(10);

  useEffect(() => {
    fetch(API_GET_ACADEMIC_SESSIONS, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(async (response) => { const payload = await response.json(); if (!response.ok || !payload.status) throw new Error(payload.message || 'Unable to load academic sessions.'); return payload; })
      .then((payload) => {
        const sessions = payload.data || [];
        const current = sessions.find((session) => session.is_active) || sessions[0];
        setAcademicSessions(sessions);
        if (current) { setSelectedSession(current.session_code); setSelectedFeeSessionId(String(current.id)); setSessionStart(new Date(`${current.start_date}T00:00:00`)); setSessionEnd(new Date(`${current.end_date}T00:00:00`)); }
      }).catch((error) => { setFeeError(error.message); setMiscError(error.message); });
  }, []);

  const getPaidFeesReport = useCallback(async () => {
    setFeeLoading(true);
    setFeeError('');
    try {
      const token = localStorage.getItem("school_token");
      const response = await fetch(API_STUDENT_FEE_REPORTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_start: format(startOfMonth(sessionStart), "yyyy-MM-dd"),
          session_end: format(endOfMonth(sessionEnd), "yyyy-MM-dd"),
          status_flag: paymentStatus.toLowerCase(),
          academic_session_id: selectedFeeSessionId,
        }),
      });
      if (response.status === 401) {
        logout();
        return;
      }
      const responseData = await response.json();
      if (!response.ok || !responseData.status) throw new Error(responseData.message || 'Unable to load the student fee report.');
      setFeeReport(responseData.data || []);
    } catch (error) {
      setFeeReport([]);
      setFeeError(error.message || 'Unable to load the student fee report.');
    } finally { setFeeLoading(false); }
  }, [logout, paymentStatus, selectedFeeSessionId, sessionEnd, sessionStart]);

  const getPaidMiscFeesReport = useCallback(async () => {
    setMiscLoading(true);
    setMiscError('');
    try {
      const token = localStorage.getItem("school_token");
      const response = await fetch(API_MISC_FEE_REPORTS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session: selectedSession,
          academic_session_id: selectedFeeSessionId,
        }),
      });
      if (response.status === 401) {
        logout();
        return;
      }
      const responseData = await response.json();
      if (!response.ok || !responseData.status) throw new Error(responseData.message || 'Unable to load the miscellaneous fee report.');
      setMiscReport(responseData.data || []);
    } catch (error) {
      setMiscReport([]);
      setMiscError(error.message || 'Unable to load the miscellaneous fee report.');
    } finally { setMiscLoading(false); }
  }, [logout, selectedFeeSessionId, selectedSession]);

  useEffect(() => {
    if (sessionStart && sessionEnd && paymentStatus && selectedFeeSessionId) void getPaidFeesReport();
  }, [getPaidFeesReport, paymentStatus, selectedFeeSessionId, sessionEnd, sessionStart]);

  useEffect(() => {
    if (selectedFeeSessionId) void getPaidMiscFeesReport();
  }, [getPaidMiscFeesReport, selectedFeeSessionId]);

  const handleTabChange = (event, newValue) => setTabIndex(newValue);

  const filteredFeeReport = feeReport.filter(
    (row) =>
      row.student_name?.toLowerCase().includes(searchStudent.toLowerCase()) &&
      row.student_father_name?.toLowerCase().includes(searchFather.toLowerCase())
  );

  const filteredMiscReport = miscReport.filter(
    (row) =>
      row.student_name?.toLowerCase().includes(searchStudent.toLowerCase()) &&
      row.student_father_name?.toLowerCase().includes(searchFather.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} centered variant="fullWidth">
          <Tab label="All Student Fee Report" />
          <Tab label="Other Charge Report" />
        </Tabs>
      </Paper>

      {(tabIndex === 0 ? feeError : miscError) && <Alert severity="error" sx={{ mb: 2 }}>{tabIndex === 0 ? feeError : miscError}</Alert>}
      {(tabIndex === 0 ? feeLoading : miscLoading) && <LinearProgress sx={{ mb: 2 }} />}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Search Student Name"
              value={searchStudent}
              onChange={(e) => setSearchStudent(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Search Father Name"
              value={searchFather}
              onChange={(e) => setSearchFather(e.target.value)}
              fullWidth
            />
          </Grid>

          {tabIndex === 0 ? (
            <>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Academic Session</InputLabel>
                  <Select value={selectedFeeSessionId} label="Academic Session" onChange={(e) => { const session = academicSessions.find((item) => String(item.id) === String(e.target.value)); setSelectedFeeSessionId(e.target.value); if (session) { setSessionStart(new Date(`${session.start_date}T00:00:00`)); setSessionEnd(new Date(`${session.end_date}T00:00:00`)); } }}>
                    {academicSessions.map((session) => <MenuItem key={session.id} value={String(session.id)}>{session.session_code}{session.is_active ? ' (Current)' : ''}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Session Start"
                    views={["year", "month"]}
                    value={sessionStart}
                    onChange={(newValue) => setSessionStart(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Session End"
                    views={["year", "month"]}
                    value={sessionEnd}
                    onChange={(newValue) => setSessionEnd(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Partially Paid">Partially Paid</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Not Posted">Not Posted</MenuItem>
                    <MenuItem value="No Amount Due">No Amount Due</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Academic Session</InputLabel>
                  <Select
                    value={selectedFeeSessionId}
                    onChange={(e) => { const chosen=academicSessions.find(item=>String(item.id)===String(e.target.value));setSelectedFeeSessionId(e.target.value);if(chosen)setSelectedSession(chosen.session_code); }}
                  >
                    {academicSessions.map((session) => (
                      <MenuItem key={session.id} value={String(session.id)}>
                        {session.session_code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Tables */}
      {tabIndex === 0 && (
        <ReportTable
          title="All Fee Report"
          columns={[
            "student_no",
            "student_name",
            "student_father_name",
            "student_mother_name",
            "class_name",
            "section_name",
            "fee_month",
            "tuition_charge",
            "magic_charge",
            "charged_amount",
            "paid_amount",
            "balance_amount",
            "fee_status",
          ]}
          data={feeLoading ? [] : filteredFeeReport}
          page={pageFee}
          rowsPerPage={rowsPerPageFee}
          setPage={setPageFee}
          setRowsPerPage={setRowsPerPageFee}
          canExport={canExport}
        />
      )}

      {tabIndex === 1 && (
        <ReportTable
           title="Other Student Charge Report"
          columns={[
            "student_no",
            "student_name",
            "student_father_name",
             "student_mother_name",
             "charge_head",
             "session_code",
             "charge_date",
             "due_date",
             "amount",
             "discount",
             "paid_amount",
             "refunded_amount",
             "balance_amount",
             "fee_status",
          ]}
          data={miscLoading ? [] : filteredMiscReport}
          page={pageMisc}
          rowsPerPage={rowsPerPageMisc}
          setPage={setPageMisc}
          setRowsPerPage={setRowsPerPageMisc}
          canExport={canExport}
        />
      )}
    </Container>
  );
}
